// Device Naming Utilities
// Based on Naming Policy v3.1
// Format: REGDST-[I]SCHNUM[T|S]DEVNUM for devices
// Format: REGDST-[I]SCHNUM for UniFi sites

// Parse device name according to naming policy: REGDST-[I]SCHNUM[T|S]DEVNUM
// Examples: BKHROM-I01S019, BKHROM-I01T005, BKHROM-01S019
// Also handles: BKH-ROM-I01S019 (with dash between region and district)
export function parseDeviceName(deviceName) {
  if (!deviceName || typeof deviceName !== 'string') {
    return {
      region: 'Unknown',
      regionCode: 'Unknown',
      district: 'Unknown',
      districtCode: 'Unknown',
      schoolType: 'Unknown',
      schoolNumber: 'Unknown',
      school: 'Unknown',
      deviceType: 'Unknown',
      deviceNumber: 'Unknown',
      fullSchool: 'Unknown',
      isValid: false
    };
  }

  const upper = deviceName.toUpperCase().trim();
  
  // Check for format: REGION-SCH[SCHOOL#][T|S][DEVICE#] (e.g., SRK-SCH002S013, BUKH-SCH001T005)
  // Also handles trailing content like _Windows_10/24/2025_4:39
  const schFormatMatch = upper.match(/^([A-Z]{3,4})-SCH(\d+)([TS])(\d+)/);
  if (schFormatMatch) {
    const regionCode = schFormatMatch[1];
    const schoolNum = schFormatMatch[2];
    const typeIndicator = schFormatMatch[3];
    const devNum = schFormatMatch[4];
    
    const region = getRegionName(regionCode);
    const deviceType = typeIndicator === 'T' ? 'Teacher' : 'Student';
    const school = `School ${schoolNum.padStart(2, '0')}`;
    const fullSchool = `School ${schoolNum}`;
    
    return {
      region,
      regionCode,
      district: 'Unknown', // No district in this format
      districtCode: 'Unknown',
      schoolType: 'School',
      schoolNumber: schoolNum,
      school,
      deviceType,
      deviceNumber: devNum,
      fullSchool,
      isValid: true
    };
  }
  
  // Check for alternative format: CODE-SCHOOL-T/WS-DEVNUM (e.g., BUKH-001-T1, BUKH-001-WS1)
  // Also handles trailing content
  const altFormatMatch = upper.match(/^([A-Z]{3,6})-(\d+)-(T|WS)(\d+)/);
  if (altFormatMatch) {
    const locationCode = altFormatMatch[1];
    const schoolNum = altFormatMatch[2];
    const typeIndicator = altFormatMatch[3];
    const devNum = altFormatMatch[4];
    
    // Try to parse location code as region or region+district
    let regionCode = 'Unknown';
    let districtCode = 'Unknown';
    
    if (locationCode.length >= 6) {
      regionCode = locationCode.substring(0, 3);
      districtCode = locationCode.substring(3, 6);
    } else if (locationCode.length === 4) {
      // Could be 4-letter district code like BUKH
      districtCode = locationCode;
      // Try to infer region from district
      if (locationCode.startsWith('BUK') || locationCode === 'BUKH') {
        regionCode = 'BKH'; // Bukhara
      } else if (locationCode.startsWith('SRK')) {
        regionCode = 'SRK'; // Surkhandarya
      } else {
        regionCode = locationCode.substring(0, 3);
      }
    } else if (locationCode.length === 3) {
      regionCode = locationCode;
    } else {
      regionCode = locationCode;
    }
    
    const region = getRegionName(regionCode);
    const district = getDistrictName(districtCode);
    const deviceType = typeIndicator === 'T' ? 'Teacher' : 'Student';
    const school = `School ${schoolNum.padStart(2, '0')}`;
    const fullSchool = `School ${schoolNum}`;
    
    return {
      region,
      regionCode,
      district,
      districtCode,
      schoolType: 'School',
      schoolNumber: schoolNum,
      school,
      deviceType,
      deviceNumber: devNum,
      fullSchool,
      isValid: true
    };
  }
  
  // Remove any dashes between region and district (e.g., BKH-ROM becomes BKHROM)
  // Find the main separator dash (the one before school info)
  let normalized = upper;
  let mainDashIndex = -1;
  
  // Look for pattern: 6+ letters, then dash, then [I]digits or just digits followed by T/S
  const match = upper.match(/^([A-Z-]{6,})-([IS]?\d+[TS]?\d*)$/);
  if (match) {
    const regionDistrict = match[1].replace(/-/g, ''); // Remove internal dashes
    const schoolAndDevice = match[2];
    normalized = regionDistrict + '-' + schoolAndDevice;
    mainDashIndex = regionDistrict.length;
  } else {
    // Fallback: find first dash after at least 6 characters
    mainDashIndex = upper.indexOf('-', 6);
  }
  
  if (mainDashIndex < 6) {
    // Invalid format
    return {
      region: 'Unknown',
      regionCode: 'Unknown',
      district: 'Unknown',
      districtCode: 'Unknown',
      schoolType: 'Unknown',
      schoolNumber: 'Unknown',
      school: 'Unknown',
      deviceType: 'Unknown',
      deviceNumber: 'Unknown',
      fullSchool: 'Unknown',
      isValid: false
    };
  }

  // Extract region (first 3 chars) and district (next 3 chars)
  const regionCode = normalized.substring(0, 3);
  const districtCode = normalized.substring(3, 6);
  
  // Extract school and device info after the main dash
  const schoolAndDevice = normalized.substring(mainDashIndex + 1);

  // Parse school type and number
  let schoolType = 'School';
  let schoolNumber = 'Unknown';
  let deviceType = 'Unknown';
  let deviceNumber = 'Unknown';
  
  // Check for IDUM: starts with I followed by digits
  if (/^I\d+/.test(schoolAndDevice)) {
    schoolType = 'IDUM';
    // Match pattern: I + digits + optional [T|S] + optional digits
    const idumMatch = schoolAndDevice.match(/^I(\d+)([TS])?(\d*)$/);
    if (idumMatch) {
      schoolNumber = idumMatch[1];
      if (idumMatch[2]) {
        deviceType = idumMatch[2] === 'T' ? 'Teacher' : 'Student';
        deviceNumber = idumMatch[3] || 'Unknown';
      }
    }
  } else {
    // Regular school: starts with digits
    // Try current format first: digits + optional [T|S] + optional digits
    const regularMatch = schoolAndDevice.match(/^(\d+)([TS])?(\d*)$/);
    if (regularMatch) {
      schoolNumber = regularMatch[1];
      if (regularMatch[2]) {
        deviceType = regularMatch[2] === 'T' ? 'Teacher' : 'Student';
        deviceNumber = regularMatch[3] || 'Unknown';
      }
    }
  }
  
  // Legacy format fallback: school number followed by T or S before final digits
  // Examples: 208T001, 208S, 45T, etc.
  if (deviceType === 'Unknown' && schoolNumber !== 'Unknown') {
    // Check if there's a T or S in the school+device string
    const legacyMatch = schoolAndDevice.match(/^(\d+)([TS])(\d*)$/);
    if (legacyMatch && legacyMatch[3]) {
      // Has digits after T/S, so T/S indicates device type
      schoolNumber = legacyMatch[1];
      deviceType = legacyMatch[2] === 'T' ? 'Teacher' : 'Student';
      deviceNumber = legacyMatch[3];
    } else {
      // Try pattern where T/S is at the end: 208T, 208S
      const legacyEndMatch = schoolAndDevice.match(/^(\d+)([TS])$/);
      if (legacyEndMatch) {
        schoolNumber = legacyEndMatch[1];
        deviceType = legacyEndMatch[2] === 'T' ? 'Teacher' : 'Student';
        deviceNumber = 'Unknown';
      }
    }
  }

  // Get full region and district names
  const region = getRegionName(regionCode);
  const district = getDistrictName(districtCode);
  if (schoolNumber !== 'Unknown' && /^\d+$/.test(schoolNumber)) {
    schoolNumber = String(parseInt(schoolNumber, 10));
  }
  const fullSchool = `${schoolType} ${schoolNumber}`;
  const school = schoolNumber !== 'Unknown' ? `${schoolType} ${schoolNumber.padStart(2, '0')}` : 'Unknown';

  return {
    region,
    regionCode,
    district,
    districtCode,
    schoolType,
    schoolNumber,
    school,
    deviceType,
    deviceNumber,
    fullSchool,
    isValid: schoolNumber !== 'Unknown'
  };
}

// Parse UniFi site name
// Format: REGDST-[I]SCHNUM or REG-DST-[I]SCHNUM
// Examples: BKHROM-I01, BKHROM-01, BKH-ROM-I01
export function parseUniFiSiteName(name) {
  if (!name || typeof name !== 'string') {
    return { 
      region: null, 
      regionCode: null,
      district: null, 
      districtCode: null,
      schoolType: null, 
      schoolNumber: null,
      school: null,
      fullSchool: null
    };
  }

  const upper = name.toUpperCase().trim();
  
  // Handle formats: REGDST-[I]SCHNUM or REG-DST-[I]SCHNUM
  // Remove any dashes between region and district to normalize
  let normalized = upper;
  let mainDashIndex = -1;
  
  // Look for pattern: 6+ letters (possibly with dashes), then dash, then school info
  const match = upper.match(/^([A-Z-]{6,})-([IS]?\d+)$/);
  if (match) {
    const regionDistrictRaw = match[1];
    const schoolInfo = match[2];
    if (regionDistrictRaw.includes('-')) {
      const rdParts = regionDistrictRaw.split('-');
      const _rc = rdParts[0];
      const _dc = rdParts.slice(1).join('');
      const _sn_raw = schoolInfo.startsWith('I') ? schoolInfo.slice(1) : schoolInfo.replace(/\D/g,'');
      const _sn = _sn_raw && /^\d+$/.test(_sn_raw) ? String(parseInt(_sn_raw, 10)) : _sn_raw;
      const _st = schoolInfo.startsWith('I') ? 'IDUM' : 'School';
      const _school = _sn ? _st + ' ' + _sn.padStart(2,'0') : null;
      return {
        region: getRegionName(_rc), regionCode: _rc,
        district: getDistrictName(_dc), districtCode: _dc,
        schoolType: _st, schoolNumber: _sn,
        school: _school, fullSchool: _sn ? _st + ' ' + _sn : null
      };
    }
    const regionDistrict = regionDistrictRaw;
    normalized = regionDistrict + '-' + schoolInfo;
    mainDashIndex = regionDistrict.length;
  } else {
    // Fallback: find first dash after at least 6 characters
    mainDashIndex = upper.indexOf('-', 6);
  }
  
  if (mainDashIndex < 6) {
    // Try without normalization for edge cases
    const parts = upper.split('-');
    if (parts.length >= 2) {
      // Could be REG-DST-SCHOOL format
      if (parts.length >= 3 && parts[0].length === 3 && parts[1].length === 3) {
        const regionCode = parts[0];
        const districtCode = parts[1];
        const schoolInfo = parts[2];
        
        // Parse school info
        let schoolType = 'School';
        let schoolNumber = schoolInfo;
        
        if (/^I\d+/.test(schoolInfo)) {
          schoolType = 'IDUM';
          schoolNumber = schoolInfo.substring(1); // Remove I
        } else {
          schoolNumber = schoolInfo.replace(/[^0-9]/g, ''); // Extract digits
        }
        
        const region = getRegionName(regionCode);
        const district = getDistrictName(districtCode);
        const fullSchool = `${schoolType} ${schoolNumber}`;
        if (schoolNumber && /^\d+$/.test(schoolNumber)) schoolNumber = String(parseInt(schoolNumber, 10));
        const school = schoolNumber ? `${schoolType} ${schoolNumber.padStart(2, '0')}` : null;
        
        return {
          region,
          regionCode,
          district,
          districtCode,
          schoolType,
          schoolNumber,
          school,
          fullSchool
        };
      }
    }
    
    return { 
      region: null, 
      regionCode: null,
      district: null, 
      districtCode: null,
      schoolType: null, 
      schoolNumber: null,
      school: null,
      fullSchool: null
    };
  }

  // Extract region (first 3 chars) and district (next 3 chars)
  const regionCode = normalized.substring(0, 3);
  const districtCode = normalized.substring(3, 6);
  
  // Extract school info after the main dash
  const schoolInfo = normalized.substring(mainDashIndex + 1);
  
  // Parse school type and number
  let schoolType = 'School';
  let schoolNumber = schoolInfo;
  
  if (/^I\d+/.test(schoolInfo)) {
    schoolType = 'IDUM';
    schoolNumber = schoolInfo.substring(1); // Remove I
  } else {
    schoolNumber = schoolInfo.replace(/[^0-9]/g, ''); // Extract digits
  }

  const region = getRegionName(regionCode);
  const district = getDistrictName(districtCode);
  const fullSchool = `${schoolType} ${schoolNumber}`;
  if (schoolNumber && /^\d+$/.test(schoolNumber)) schoolNumber = String(parseInt(schoolNumber, 10));
  const school = schoolNumber ? `${schoolType} ${schoolNumber.padStart(2, '0')}` : null;

  return {
    region,
    regionCode,
    district,
    districtCode,
    schoolType,
    schoolNumber,
    school,
    fullSchool
  };
}

// Region code mapping from naming policy document
export function getRegionName(code) {
  if (!code) return null;
  const regionMap = {
    // Canonical codes
    'KRP':'Karakalpakstan','ANR':'Andijan','BKH':'Bukhara','KSK':'Kashkadarya',
    'JZK':'Jizzakh','NVY':'Navoi','NMN':'Namangan','SMR':'Samarkand',
    'SRK':'Surkhandarya','SRD':'Syrdarya','TSR':'Tashkent','FRG':'Ferghana',
    'KHR':'Khorezm','TSC':'Tashkent city',
    // Variant codes
    'KAR':'Karakalpakstan','KRK':'Karakalpakstan','QQR':'Karakalpakstan','KKP':'Karakalpakstan',
    'AND':'Andijan','ADJ':'Andijan','ANI':'Andijan','ARN':'Andijan',
    'BHK':'Bukhara','BUK':'Bukhara','BXO':'Bukhara','BUH':'Bukhara',
    'KAS':'Kashkadarya','QAS':'Kashkadarya','KSD':'Kashkadarya','KSH':'Kashkadarya',
    'JIZ':'Jizzakh','DZI':'Jizzakh','JZX':'Jizzakh',
    'NAV':'Navoi','NVO':'Navoi',
    'NAM':'Namangan','NGN':'Namangan','MAK':'Namangan',
    'SAM':'Samarkand','SAR':'Samarkand',
    'SUR':'Surkhandarya','SRH':'Surkhandarya','SKD':'Surkhandarya',
    'SYR':'Syrdarya','SDA':'Syrdarya',
    'TSH':'Tashkent','TTS':'Tashkent',
    'TAC':'Tashkent city','JSK':'Tashkent city',
    'FER':'Ferghana','FAR':'Ferghana',
    'KHO':'Khorezm','XOR':'Khorezm',
    'UNK':'Unclassified',
  };
  // Returns null for unknown codes (caller can detect unrecognised regions)
  const upper = typeof code === 'string' ? code.toUpperCase() : code;
  return regionMap[code] || regionMap[upper] || null;
}

// District code mapping from naming policy document
export function getDistrictName(code) {
  const districtMap = {
    'NUK': 'Nukus city',
    'BER': 'Beruni',
    'TAK': 'Takhtakupir',
    'MUY': 'Muynak',
    'ANC': 'Andijan city',
    'ASA': 'Asaka',
    'BUS': 'Buston',
    'ULU': 'Ulugnor',
    'BUK': 'Bukhara city',
    'BUKH': 'Bukhara city', // Variant spelling
    'KAG': 'Kagan city',
    'ROM': 'Romitan',
    'KAR': 'Karshi city',
    'KRD': 'Karshi',
    'GUZ': 'Guzor',
    'SHA': 'Shakhrisabs',
    'ZAR': 'Zarbdar',
    'GAL': 'Gallaorol',
    'JIZ': 'Jizzakh city',
    'NAV': 'Navoi city',
    'KRM': 'Karmana',
    'KYZ': 'Kyziltepa',
    'UCK': 'Uchkurgan',
    'POP': 'Pop',
    'CHU': 'Chust',
    'SAM': 'Samarkand city',
    'OKD': 'Okdaryo',
    'JOM': 'Jomboy',
    'ANG': 'Angor',
    'SHO': 'Shorchi',
    'TER': 'Termiz',
    'TRM': 'Termiz city',
    'BOY': 'Boyovut',
    'GUL': 'Gulistan city',
    'SYR': 'Syrdarya',
    'BEK': 'Bekobod city',
    'PIS': 'Piskent',
    'AKK': 'Akkurgan',
    'BES': 'Beshariq',
    'ALT': 'Altyarik',
    'DAN': 'Dangara',
    'UZB': 'Uzbekistan',
    'FUR': 'Furqat',
    'URG': 'Urganch city',
    'URD': 'Urganch',
    'KHI': 'Khiva city',
    'KHA': 'Khazorap',
    'SER': 'Sergeli',
    'UCD': 'Uchtepa',
    // Alias codes — non-standard but observed in real data
    'SCHO': 'Shorchi',      // C3 server SRK-SCHO-I36
    'CUK':  'Uchkurgan',    // NMNCUK-010S050
    'RKM':  'Karmana',      // NVYRKM-021T004
    'UDC':  'Uchtepa',      // TSCUDC-287S030
    'URGS': 'Urganch city', // KHRURGS-016S022
  };
  
  return districtMap[code] || code;
}

// Convenience functions for backward compatibility and easy access
export function extractRegion(deviceName) {
  const parsed = parseDeviceName(deviceName);
  return parsed.region;
}

export function extractRegionCode(deviceName) {
  const parsed = parseDeviceName(deviceName);
  return parsed.regionCode;
}

export function extractDistrict(deviceName) {
  const parsed = parseDeviceName(deviceName);
  return parsed.district;
}

export function extractDistrictCode(deviceName) {
  const parsed = parseDeviceName(deviceName);
  return parsed.districtCode;
}

export function extractSchool(deviceName) {
  const parsed = parseDeviceName(deviceName);
  return parsed.fullSchool;
}

export function extractSchoolNumber(deviceName) {
  const parsed = parseDeviceName(deviceName);
  return parsed.schoolNumber;
}

export function extractSchoolType(deviceName) {
  const parsed = parseDeviceName(deviceName);
  return parsed.schoolType;
}

export function extractDeviceType(deviceName) {
  const parsed = parseDeviceName(deviceName);
  return parsed.deviceType;
}

export function extractDeviceNumber(deviceName) {
  const parsed = parseDeviceName(deviceName);
  return parsed.deviceNumber;
}

// ─── Serial number overrides ─────────────────────────────────────────────────
// Delete entries as admin renames are completed. Keep in sync with server.js.
export const SERIAL_OVERRIDES = {
  'NXBCGER00354338B8B7600': { regionCode:'NMN', region:'Namangan',     districtCode:'UCK', district:'Uchkurgan',         schoolType:'School', schoolNumber:'010', deviceType:'Student', deviceNumber:'018' },
  'NXBCGER0035433C0017600': { regionCode:'NMN', region:'Namangan',     districtCode:'CHU', district:'Chust',             schoolType:'School', schoolNumber:'058', deviceType:'Student', deviceNumber:'023' },
  'NXBCGER00354338BD87600': { regionCode:'NMN', region:'Namangan',     districtCode:'CHU', district:'Chust',             schoolType:'School', schoolNumber:'058', deviceType:'Student', deviceNumber:'050' },
  'NXBCGER00354338B6F7600': { regionCode:'NMN', region:'Namangan',     districtCode:'CHU', district:'Chust',             schoolType:'School', schoolNumber:'058', deviceType:'Student', deviceNumber:'016' },
  'NXB7XER0065394560E7600': { regionCode:'BKH', region:'Bukhara',      districtCode:'BUK', district:'Bukhara city',      schoolType:'School', schoolNumber:'045', deviceType:'Teacher', deviceNumber:'006' },
  'NXBCGER0035433BD557600': { regionCode:'TSC', region:'Tashkent city', districtCode:'UCD', district:'Uchtepa',          schoolType:'School', schoolNumber:'287', deviceType:'Student', deviceNumber:'030' },
  'NXB7XER00654421DC37600': { regionCode:'NVY', region:'Navoi',        districtCode:'KRM', district:'Karmana',           schoolType:'School', schoolNumber:'021', deviceType:'Teacher', deviceNumber:'004' },
  'NXBCGER00354338B927600': { regionCode:'NMN', region:'Namangan',     districtCode:'UCK', district:'Uchkurgan',         schoolType:'School', schoolNumber:'010', deviceType:'Student', deviceNumber:'050' },
  'NXBCGER0035433BDEC7600': { regionCode:'JZK', region:'Jizzakh',      districtCode:'JIZ', district:'Jizzakh city',     schoolType:'School', schoolNumber:'001', deviceType:'Student', deviceNumber:'070' },
  'NXBCGER0035433BF947600': { regionCode:'SRD', region:'Syrdarya',     districtCode:'SYR', district:'Syrdarya district', schoolType:'School', schoolNumber:'001', deviceType:'Student', deviceNumber:'010' },
  'NXBCGER00354338D737600': { regionCode:'TSR', region:'Tashkent',     districtCode:'AKK', district:'Akkurgan',          schoolType:'School', schoolNumber:'018', deviceType:'Student', deviceNumber:'026' },
  'NXBCGER00354338A4D7600': { regionCode:'NMN', region:'Namangan',     districtCode:'POP', district:'Pop',               schoolType:'School', schoolNumber:'001', deviceType:'Teacher', deviceNumber:'016' },
  'NXBCGER0035471B86F7600': { regionCode:'KSK', region:'Kashkadarya',  districtCode:'KAR', district:'Karshi city',      schoolType:'School', schoolNumber:'008', deviceType:'Teacher', deviceNumber:'001' },
  'NXBCGER0035433BED57600': { regionCode:'SMR', region:'Samarkand',    districtCode:'OKD', district:'Okdaryo',           schoolType:'IDUM',   schoolNumber:'47',  deviceType:'Student', deviceNumber:'027' },
  'NXBCGER0035471B7717600': { regionCode:'SRK', region:'Surkhandarya', districtCode:'SHO', district:'Shorchi',           schoolType:'IDUM',   schoolNumber:'036', deviceType:'Student', deviceNumber:'057' },
  'NXBCGER003539453067600': { regionCode:'SRK', region:'Surkhandarya', districtCode:'TRM', district:'Termiz city',       schoolType:'School', schoolNumber:'002', deviceType:'Student', deviceNumber:'002' },
  'NXBCGER003539452B87600': { regionCode:'SRK', region:'Surkhandarya', districtCode:'TRM', district:'Termiz city',       schoolType:'School', schoolNumber:'002', deviceType:'Student', deviceNumber:'008' },
  'NXBCGER0035394530D7600': { regionCode:'SRK', region:'Surkhandarya', districtCode:'TRM', district:'Termiz city',       schoolType:'School', schoolNumber:'002', deviceType:'Student', deviceNumber:'011' },
  'NXBCGER003539452E17600': { regionCode:'SRK', region:'Surkhandarya', districtCode:'TRM', district:'Termiz city',       schoolType:'School', schoolNumber:'002', deviceType:'Student', deviceNumber:'012' },
  'NXBCGER003539453347600': { regionCode:'SRK', region:'Surkhandarya', districtCode:'TRM', district:'Termiz city',       schoolType:'School', schoolNumber:'002', deviceType:'Student', deviceNumber:'024' },
  'NXBCGER003539453557600': { regionCode:'SRK', region:'Surkhandarya', districtCode:'TRM', district:'Termiz city',       schoolType:'School', schoolNumber:'002', deviceType:'Student', deviceNumber:'027' },
  'NXBCGER0035394533A7600': { regionCode:'SRK', region:'Surkhandarya', districtCode:'TRM', district:'Termiz city',       schoolType:'School', schoolNumber:'002', deviceType:'Student', deviceNumber:'028' },
  'NXBCGER003539452F47600': { regionCode:'SRK', region:'Surkhandarya', districtCode:'TRM', district:'Termiz city',       schoolType:'School', schoolNumber:'002', deviceType:'Student', deviceNumber:'029' },
  'NXBCGER003539453147600': { regionCode:'SRK', region:'Surkhandarya', districtCode:'TRM', district:'Termiz city',       schoolType:'School', schoolNumber:'002', deviceType:'Student', deviceNumber:'036' },
  'NXBCGER003539453077600': { regionCode:'SRK', region:'Surkhandarya', districtCode:'TRM', district:'Termiz city',       schoolType:'School', schoolNumber:'002', deviceType:'Student', deviceNumber:'037' },
  'NXBCGER003539453487600': { regionCode:'SRK', region:'Surkhandarya', districtCode:'TRM', district:'Termiz city',       schoolType:'School', schoolNumber:'002', deviceType:'Student', deviceNumber:'040' },
  'NXBCGER003539452FA7600': { regionCode:'SRK', region:'Surkhandarya', districtCode:'TRM', district:'Termiz city',       schoolType:'School', schoolNumber:'002', deviceType:'Student', deviceNumber:'041' },
  'NXB7XER0065394563A7600': { regionCode:'SRK', region:'Surkhandarya', districtCode:'TRM', district:'Termiz city',       schoolType:'School', schoolNumber:'002', deviceType:'Teacher', deviceNumber:'014' },
  'NXBCGER003539452957600': { regionCode:'SRK', region:'Surkhandarya', districtCode:'TRM', district:'Termiz city',       schoolType:'School', schoolNumber:'003', deviceType:'Student', deviceNumber:'058' },
  'NXB7XER0065394560A7600': { regionCode:'SRK', region:'Surkhandarya', districtCode:'TRM', district:'Termiz city',       schoolType:'School', schoolNumber:'003', deviceType:'Teacher', deviceNumber:'019' },
  // ── Additional bad device names found in audit ──────────────────────────────
  'NXBCGER0035471B8207600': { regionCode:'SRK', region:'Surkhandarya', districtCode:'SHO', district:'Shorchi',  schoolType:'IDUM',   schoolNumber:'36',  deviceType:'Student', deviceNumber:'035' }, // SRKSHO-I0S035 (I0 should be I036)
  'NXBCGER0035433BDB87600': { regionCode:'NVY', region:'Navoi',        districtCode:'KRM', district:'Karmana',  schoolType:'School', schoolNumber:'21',  deviceType:'Student', deviceNumber:'034' }, // NVYKRM-012S034 (012 likely transposed, should be 021)
  // ── Unclassified — serial known but school cannot be determined ─────────────
  // Assign to explicit 'Unclassified' so they appear visibly rather than being dropped.
  // Admin should identify the correct school and add a proper override or rename the device.
  'NXBCGER00354338C0A7600': { regionCode:'UNK', region:'Unclassified', districtCode:'UNK', district:'Unclassified', schoolType:'School', schoolNumber:'000', deviceType:'Unknown', deviceNumber:'000' }, // RaxmatovaLaziza
  'NXB7XER0065394562E7600': { regionCode:'UNK', region:'Unclassified', districtCode:'UNK', district:'Unclassified', schoolType:'School', schoolNumber:'000', deviceType:'Unknown', deviceNumber:'000' }, // DESKTOP-5O7NF4R
  'NXBCGER00354338A467600': { regionCode:'UNK', region:'Unclassified', districtCode:'UNK', district:'Unclassified', schoolType:'School', schoolNumber:'000', deviceType:'Unknown', deviceNumber:'000' }, // LAPTOP-65UB9UEJ
  'NXBCGER00354338D507600': { regionCode:'UNK', region:'Unclassified', districtCode:'UNK', district:'Unclassified', schoolType:'School', schoolNumber:'000', deviceType:'Unknown', deviceNumber:'000' }, // LAPTOP-LS1Q6OQE
};

export function parseDeviceNameWithSerial(deviceName, serialNumber) {
  if (serialNumber && SERIAL_OVERRIDES[serialNumber]) {
    const o = SERIAL_OVERRIDES[serialNumber];
    const sn = (o.schoolNumber && /^\d+$/.test(o.schoolNumber))
      ? String(parseInt(o.schoolNumber, 10))
      : (o.schoolNumber || 'Unknown');
    const school = o.schoolType + ' ' + sn.padStart(2, '0');
    return { ...o, schoolNumber: sn, school, fullSchool: o.schoolType + ' ' + sn, isValid: true, isSerialOverride: true };
  }
  return parseDeviceName(deviceName);
}


// Get all unique regions from device list
export function getUniqueRegions(devices) {
  const regions = new Set();
  devices.forEach(device => {
    const parsed = parseDeviceName(device.deviceName || device.name);
    if (parsed.isValid && parsed.regionCode !== 'Unknown') {
      regions.add(parsed.regionCode);
    }
  });
  return Array.from(regions).sort();
}

// Get unique districts for a specific region
export function getDistrictsForRegion(devices, regionCode) {
  const districts = new Set();
  devices.forEach(device => {
    const parsed = parseDeviceName(device.deviceName || device.name);
    if (parsed.isValid && parsed.regionCode === regionCode && parsed.districtCode !== 'Unknown') {
      districts.add(parsed.districtCode);
    }
  });
  return Array.from(districts).sort();
}

// Get unique schools for a specific region and district
export function getSchoolsForDistrict(devices, regionCode, districtCode) {
  const schools = new Set();
  devices.forEach(device => {
    const parsed = parseDeviceName(device.deviceName || device.name);
    if (parsed.isValid && 
        parsed.regionCode === regionCode && 
        parsed.districtCode === districtCode &&
        parsed.schoolNumber !== 'Unknown') {
      schools.add(`${parsed.schoolType}-${parsed.schoolNumber}`);
    }
  });
  return Array.from(schools).sort();
}

// Filter devices by region, district, and school
export function filterDevices(devices, filters) {
  const { region, district, school } = filters;
  
  return devices.filter(device => {
    const parsed = parseDeviceName(device.deviceName || device.name);
    
    if (!parsed.isValid) return false;
    
    if (region && parsed.regionCode !== region) return false;
    if (district && parsed.districtCode !== district) return false;
    if (school) {
      const schoolKey = `${parsed.schoolType}-${parsed.schoolNumber}`;
      if (schoolKey !== school) return false;
    }
    
    return true;
  });
}
